import React, { useState, useEffect, useCallback } from 'react';
import { X, ShoppingCart, Copy, Check, Clock, FlaskConical } from 'lucide-react';
import { canShowPopup, registerPopupShown, markPopupDismissed, wasPopupShown } from '../lib/popupManager';
import { useTrackCTA } from '../lib/useTrackCTA';

const POPUP_ID = 'apr_peptides_25';
const AFFILIATE_URL = 'https://www.americanpeptideresearch.net/ref/16/';
const DISCOUNT_CODE = 'NINJA';

export default function APRPromoPopup() {
  const [show, setShow] = useState(false);
  const track = useTrackCTA();
  const [copied, setCopied] = useState(false);

  const dismiss = useCallback(() => {
    setShow(false);
    markPopupDismissed(POPUP_ID);
  }, []);

  useEffect(() => {
    if (wasPopupShown(POPUP_ID) || localStorage.getItem('apr_promo_seen')) return;

    const tryShow = () => {
      if (canShowPopup(POPUP_ID)) {
        setShow(true);
        registerPopupShown(POPUP_ID);
      }
    };

    const timer = setTimeout(tryShow, 10000);

    const handleScroll = () => {
      const scrollPct = window.scrollY / (document.documentElement.scrollHeight - window.innerHeight);
      if (scrollPct > 0.30) {
        tryShow();
        window.removeEventListener('scroll', handleScroll);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleCopy = async () => {
    track('popup_copy_code', DISCOUNT_CODE);
    try {
      await navigator.clipboard.writeText(DISCOUNT_CODE);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleShop = () => {
    track('popup_shop_apr', AFFILIATE_URL);
    localStorage.setItem('apr_promo_seen', 'true');
    window.open(AFFILIATE_URL, '_blank', 'noopener');
    dismiss();
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" data-testid="apr-promo-popup">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/75 backdrop-blur-md" onClick={dismiss} />

      {/* Modal — glassmorphism card */}
      <div
        className="relative w-full max-w-sm overflow-hidden animate-in fade-in zoom-in-95 duration-300"
        style={{
          background: 'linear-gradient(160deg, rgba(10,30,20,0.95), rgba(5,15,10,0.98))',
          border: '1px solid rgba(200,255,0,0.15)',
          borderRadius: '16px',
          boxShadow: '0 25px 60px rgba(0,0,0,0.6), 0 0 80px rgba(200,255,0,0.04)',
        }}
      >
        {/* Close */}
        <button
          onClick={dismiss}
          className="absolute top-3 right-3 z-20 w-8 h-8 flex items-center justify-center rounded-full bg-zinc-900/60 border border-zinc-700/50 text-zinc-400 hover:text-white hover:border-zinc-500 transition-all"
          data-testid="apr-promo-close"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="px-6 pt-6 pb-5">
          {/* Badge */}
          <div className="flex justify-center mb-5">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 text-[10px] uppercase tracking-[0.15em] font-bold border rounded-full"
              style={{ color: '#c8ff00', borderColor: 'rgba(200,255,0,0.3)', backgroundColor: 'rgba(200,255,0,0.08)' }}
            >
              <Clock className="w-3 h-3" />
              Limited Time Offer
            </span>
          </div>

          {/* Big discount */}
          <div className="text-center mb-4">
            <div className="flex items-center justify-center gap-2 mb-1">
              <span className="text-2xl">
                <FlaskConical className="w-6 h-6" style={{ color: '#f97316' }} />
              </span>
              <span
                className="text-5xl sm:text-6xl font-black"
                style={{ color: '#c8ff00', fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '-0.02em' }}
                data-testid="discount-amount"
              >
                25%
              </span>
              <span className="text-zinc-400 text-lg font-bold uppercase tracking-wider mt-2">OFF</span>
            </div>
            <p className="text-zinc-400 text-sm leading-relaxed max-w-xs mx-auto">
              Your discount is <strong className="text-zinc-200">applied automatically</strong> when you shop research-grade peptides from <strong className="text-zinc-200">American Peptide Research</strong> through our link.
            </p>
          </div>

          {/* Discount code box */}
          <div
            className="flex items-center justify-between p-4 mb-5 cursor-pointer"
            onClick={handleCopy}
            style={{
              border: '1px solid rgba(200,255,0,0.25)',
              borderRadius: '8px',
              backgroundColor: 'rgba(200,255,0,0.04)',
            }}
            data-testid="discount-code-box"
          >
            <div>
              <span className="block text-zinc-500 text-[10px] uppercase tracking-[0.15em] mb-0.5">Backup code — only if checkout asks</span>
              <span
                className="block text-xl font-black tracking-wider"
                style={{ color: '#c8ff00', fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '0.08em' }}
                data-testid="discount-code"
              >
                {DISCOUNT_CODE}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-zinc-400 text-lg font-bold" style={{ color: '#c8ff00' }}>-25%</span>
              {copied ? (
                <Check className="w-5 h-5 text-green-400" />
              ) : (
                <Copy className="w-5 h-5 text-zinc-500 hover:text-zinc-300 transition-colors" />
              )}
            </div>
          </div>

          {/* CTA */}
          <button
            onClick={handleShop}
            className="w-full py-3.5 flex items-center justify-center gap-2 text-sm font-black uppercase tracking-wider transition-all duration-200 hover:opacity-90"
            style={{
              backgroundColor: '#c8ff00',
              color: '#000',
              fontFamily: 'Bebas Neue, sans-serif',
              letterSpacing: '0.08em',
              borderRadius: '6px',
              fontSize: '0.95rem',
            }}
            data-testid="shop-apr-btn"
          >
            <ShoppingCart className="w-4 h-4" />
            Shop American Peptide Research
          </button>

          {/* Trust badges */}
          <p className="text-zinc-600 text-[10px] text-center mt-3 tracking-wide">
            99%+ purity &bull; 3rd-party tested &bull; COA included
          </p>
        </div>
      </div>
    </div>
  );
}
